/**
 * German Number Parsing Utility
 *
 * Parses various number formats and normalizes to JavaScript number type.
 * Accepts both German format (12.300,33) and standard formats (12300.33, 12,300.33).
 */

/**
 * @example
 * parseGermanNumber("12.300,33")    // 12300.33 ✅ German format
 * parseGermanNumber("12300.33")     // 12300.33 ✅ Auto-convert
 * parseGermanNumber("12,300.33")    // 12300.33 ✅ Auto-convert (EN format)
 * parseGermanNumber("-1.000,50")    // -1000.5  ✅ Negative numbers
 * parseGermanNumber(12300.33)       // 12300.33 ✅ Already a number
 * parseGermanNumber("")             // null     ✅ Empty string
 * parseGermanNumber("abc")          // null     ❌ Invalid input
 */
export function parseGermanNumber(input: string | number | null | undefined): number | null {
  // Handle null/undefined
  if (input == null) {
    return null;
  }

  // Already a number - return as-is
  if (typeof input === 'number') {
    return isNaN(input) ? null : input;
  }

  // Convert to string and trim
  const str = String(input).trim();

  // Empty string -> null
  if (str === '' || str === '-') {
    return null;
  }

  // Remove any whitespace within the number
  const cleaned = str.replace(/\s/g, '');

  // Detect format based on comma and dot positions
  const lastComma = cleaned.lastIndexOf(',');
  const lastDot = cleaned.lastIndexOf('.');

  let normalized: string;

  if (lastComma === -1 && lastDot === -1) {
    // No separators: "12300" -> "12300"
    normalized = cleaned;
  } else if (lastComma === -1) {
    // Only dots: could be "12.300" (German thousands) or "12.33" (EN decimal)
    // Heuristic: if dot is in last 3 chars and there are 1-2 digits after, it's a decimal
    const dotsCount = (cleaned.match(/\./g) || []).length;
    const digitsAfterLastDot = cleaned.length - lastDot - 1;

    if (dotsCount === 1 && digitsAfterLastDot <= 2 && digitsAfterLastDot > 0) {
      // Likely EN decimal format: "12.33" -> "12.33"
      normalized = cleaned;
    } else {
      // German thousands separator: "12.300" -> "12300"
      normalized = cleaned.replace(/\./g, '');
    }
  } else if (lastDot === -1) {
    // Only commas: could be "12,300" (EN thousands) or "12,33" (German decimal)
    const commasCount = (cleaned.match(/,/g) || []).length;
    const digitsAfterLastComma = cleaned.length - lastComma - 1;

    if (commasCount === 1 && digitsAfterLastComma <= 2 && digitsAfterLastComma > 0) {
      // Likely German decimal: "12,33" -> "12.33"
      normalized = cleaned.replace(',', '.');
    } else {
      // EN thousands separator: "12,300" -> "12300"
      normalized = cleaned.replace(/,/g, '');
    }
  } else {
    // Both comma and dot present
    if (lastComma > lastDot) {
      // German format: "12.300,33" -> "12300.33"
      normalized = cleaned.replace(/\./g, '').replace(',', '.');
    } else {
      // EN format: "12,300.33" -> "12300.33"
      normalized = cleaned.replace(/,/g, '');
    }
  }

  // Parse the normalized string
  const result = parseFloat(normalized);

  // Return null if result is NaN
  return isNaN(result) ? null : result;
}

/**
 * Yup transform function for German number parsing.
 * Use in Yup schemas: yup.number().transform(yupGermanNumberTransform)
 */
export function yupGermanNumberTransform(
  originalValue: string | number | null | undefined,
): number | null | undefined {
  // If originalValue is already null/undefined, preserve it
  if (originalValue == null || originalValue === '') {
    return null;
  }

  // Parse using the main parser
  const parsed = parseGermanNumber(originalValue);

  // Return parsed value or undefined for invalid (Yup handles undefined as validation failure)
  return parsed !== null ? parsed : undefined;
}
